// MARK://dhk8/admin-shell/components/SatCodePicker
// Combobox for the SAT "clave de producto/servicio" (claveProdServ, c_ClaveProdServ
// of the CFDI 4.0 catalog). Search-as-you-type over a small CURATED list of common
// food & beverage codes embedded below, while still allowing a free-typed code so
// the user is never blocked by the short list.
//
// onChange receives whatever is in the box (a picked code or a free one). The
// suggestion list filters by code OR description (accent-insensitive).
//
// Self-contained: React + lucide-react only; scoped `nui-*` CSS.
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { ChevronDown, Search } from 'lucide-react'

export interface SatCode {
  code: string
  label: string
}

// Curated subset of c_ClaveProdServ relevant to restaurants / food service.
// Not exhaustive — free typing covers everything else.
const SAT_CODES: SatCode[] = [
  { code: '50000000', label: 'Alimentos, bebidas y tabaco' },
  { code: '50190000', label: 'Alimentos preparados y conservados' },
  { code: '50192100', label: 'Comidas preparadas' },
  { code: '50201700', label: 'Café y té' },
  { code: '50202301', label: 'Agua' },
  { code: '50202306', label: 'Bebidas no alcohólicas / refrescos' },
  { code: '50202309', label: 'Bebidas energéticas' },
  { code: '50202310', label: 'Jugos de frutas o verduras' },
  { code: '50202201', label: 'Cerveza' },
  { code: '50202203', label: 'Vino' },
  { code: '50202204', label: 'Licores y destilados' },
  { code: '50161800', label: 'Chocolate y dulces / confitería' },
  { code: '50181900', label: 'Pan y productos de panadería' },
  { code: '50131600', label: 'Quesos' },
  { code: '50112000', label: 'Carnes y aves de corral' },
  { code: '50121500', label: 'Pescados y mariscos' },
  { code: '50221300', label: 'Botanas / snacks' },
  { code: '90101500', label: 'Servicio de comida en restaurante' },
  { code: '90101501', label: 'Servicio de banquetes' },
  { code: '90101600', label: 'Servicio de comida para llevar' },
  { code: '90101603', label: 'Servicio de comida rápida' },
]

const SAT_PICKER_CSS = `
.nui-sat { position: relative; }
.nui-sat__field {
  display: flex; align-items: center; gap: var(--pos-space-2, 8px);
  min-height: 44px; padding: 0 var(--pos-space-3, 12px);
  border: 1px solid var(--pos-separator); border-radius: var(--pos-radius-md, 8px);
  background: var(--pos-canvas); color: var(--pos-fg);
}
.nui-sat__field.is-open { border-color: var(--pos-tint); box-shadow: 0 0 0 3px var(--pos-tint-soft); }
.nui-sat__field svg { flex-shrink: 0; color: var(--pos-fg-tertiary); }
.nui-sat__input {
  flex: 1; min-width: 0; border: none; outline: none; background: transparent;
  color: var(--pos-fg); font-family: var(--font-sans); font-size: var(--pos-text-sm, 13px);
}
.nui-sat__caret {
  display: inline-flex; padding: 2px; border: none; background: transparent;
  color: var(--pos-fg-tertiary); cursor: pointer;
}
.nui-sat__menu {
  position: absolute; z-index: var(--pos-z-sheet, 200);
  top: calc(100% + 4px); left: 0; right: 0;
  max-height: 260px; overflow-y: auto;
  background: var(--pos-surface); color: var(--pos-fg);
  border: 1px solid var(--pos-separator-strong);
  border-radius: var(--pos-radius-md, 8px);
  box-shadow: var(--pos-shadow-3);
  padding: var(--pos-space-1, 4px);
}
.nui-sat__opt {
  display: flex; flex-direction: column; gap: 1px;
  width: 100%; text-align: left; cursor: pointer;
  padding: var(--pos-space-2, 8px) var(--pos-space-3, 12px);
  border: none; border-radius: var(--pos-radius-sm, 6px);
  background: transparent; color: var(--pos-fg);
  font-family: var(--font-sans);
}
.nui-sat__opt:hover, .nui-sat__opt.is-active { background: var(--pos-tint-soft); }
.nui-sat__opt.is-free { background: var(--pos-elevated); }
.nui-sat__opt-code { font-size: var(--pos-text-sm, 13px); font-weight: var(--pos-weight-semibold, 600); }
.nui-sat__opt-label { font-size: var(--pos-text-xs, 12px); color: var(--pos-fg-tertiary); }
.nui-sat__empty { padding: var(--pos-space-3, 12px); font-size: var(--pos-text-xs, 12px); color: var(--pos-fg-tertiary); text-align: center; }
`

function norm(s: string): string {
  return s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()
}

export interface SatCodePickerProps {
  value: string
  onChange: (code: string) => void
}

export default function SatCodePicker({ value, onChange }: SatCodePickerProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const rootRef = useRef<HTMLDivElement>(null)
  const listId = useId()

  // The text shown in the box: while open we edit `query`, otherwise we mirror `value`.
  const text = open ? query : value

  const matches = useMemo(() => {
    const q = norm(query.trim())
    if (!q) return SAT_CODES
    return SAT_CODES.filter((c) => norm(c.code).includes(q) || norm(c.label).includes(q))
  }, [query])

  // A free code typed by the user that isn't an exact match in the list.
  const trimmed = query.trim()
  const showFree = trimmed.length > 0 && !SAT_CODES.some((c) => c.code === trimmed)

  useEffect(() => {
    if (!open) return
    function onDocClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close()
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  function openMenu() {
    setQuery(value)
    setActive(0)
    setOpen(true)
  }
  function close() {
    setOpen(false)
  }
  function pick(code: string) {
    onChange(code)
    setOpen(false)
  }

  // Flattened option list (free option first when present), for keyboard nav.
  const options: { code: string; free: boolean }[] = [
    ...(showFree ? [{ code: trimmed, free: true }] : []),
    ...matches.map((m) => ({ code: m.code, free: false })),
  ]

  function onKeyDown(e: React.KeyboardEvent) {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) { openMenu(); return }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, options.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)) }
    else if (e.key === 'Enter') {
      e.preventDefault()
      const opt = options[active]
      if (opt) pick(opt.code)
      else if (trimmed) pick(trimmed)
    } else if (e.key === 'Escape') { e.preventDefault(); close() }
  }

  return (
    <div className="nui-sat" ref={rootRef}>
      <style>{SAT_PICKER_CSS}</style>

      <div className={`nui-sat__field${open ? ' is-open' : ''}`}>
        <Search size={16} aria-hidden />
        <input
          className="nui-sat__input"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          placeholder="Buscar o teclear clave (ej. 50202306)"
          value={text}
          onFocusCapture={() => { if (!open) openMenu() }}
          onChange={(e) => { setQuery(e.target.value); setActive(0); if (!open) setOpen(true) }}
          onKeyDown={onKeyDown}
        />
        <button
          type="button"
          className="nui-sat__caret"
          aria-label={open ? 'Cerrar' : 'Abrir lista'}
          tabIndex={-1}
          onClick={() => (open ? close() : openMenu())}
        >
          <ChevronDown size={16} aria-hidden />
        </button>
      </div>

      {open && (
        <div className="nui-sat__menu" id={listId} role="listbox">
          {options.length === 0 ? (
            <div className="nui-sat__empty">Sin coincidencias. Teclea una clave para usarla.</div>
          ) : (
            options.map((opt, i) => {
              const meta = opt.free ? null : SAT_CODES.find((c) => c.code === opt.code)
              return (
                <button
                  key={`${opt.code}-${opt.free ? 'free' : 'list'}`}
                  type="button"
                  role="option"
                  aria-selected={opt.code === value}
                  className={`nui-sat__opt${i === active ? ' is-active' : ''}${opt.free ? ' is-free' : ''}`}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => pick(opt.code)}
                >
                  <span className="nui-sat__opt-code">{opt.code}</span>
                  <span className="nui-sat__opt-label">{opt.free ? 'Usar esta clave (libre)' : meta?.label}</span>
                </button>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}
